import React, { useState, useEffect } from 'react';
import { Form, Input, InputNumber, Select, Button, Table, message, Popconfirm, Row, Col, Tooltip } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import api from '../../api';
import { useTranslations } from '../../lang';

const { TextArea } = Input;

const CROP_TASK_EVENTS = [
    { value: 'before_sowing', label: 'Voor zaaien' },
    { value: 'after_sowing', label: 'Na zaaien' },
    { value: 'before_transplanting', label: 'Voor uitplanten' },
    { value: 'after_transplanting', label: 'Na uitplanten' },
    { value: 'before_harvesting', label: 'Voor oogsten' },
    { value: 'after_harvesting', label: 'Na oogsten' },
];

export default function CropTasksForm({ cropId, isDirectSown, isSeedling, hasTeam }) {
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm();
    const { translate } = useTranslations();

    // Filter events based on whether crop is directly sown
    const availableEvents = CROP_TASK_EVENTS.filter(event => {
        if (isDirectSown) {
            // Direct sown crops don't have transplanting
            return !event.value.includes('transplanting');
        }
        return true;
    }).map(event => ({
        value: event.value,
        label: translate(event.label)
    }));

    useEffect(() => {
        if (cropId) {
            loadTasks();
        }
    }, [cropId]);

    const loadTasks = async () => {
        if (!cropId) return;
        setLoading(true);
        try {
            const data = await api.getCropTasks(cropId);
            setTasks(data);
        } catch (error) {
            console.error('Failed to load crop tasks:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAddTask = async (values) => {
        try {
            await api.createCropTask(cropId, values);
            message.success(translate('Taak toegevoegd'));
            form.resetFields();
            await loadTasks();
        } catch (error) {
            message.error(translate('Kon taak niet toevoegen'));
        }
    };

    const handleFormSubmit = (e) => {
        e.preventDefault();
        e.stopPropagation();
        form.submit();
    };

    const handleDeleteTask = async (taskId) => {
        try {
            await api.deleteCropTask(cropId, taskId);
            message.success(translate('Taak verwijderd'));
            loadTasks();
        } catch (error) {
            message.error(translate('Kon taak niet verwijderen'));
        }
    };

    const getEventLabel = (event) => {
        const eventItem = CROP_TASK_EVENTS.find(e => e.value === event);
        return eventItem ? translate(eventItem.label) : event;
    };

    const columns = [
        {
            title: translate('Titel'),
            dataIndex: 'title',
            key: 'title',
        },
        {
            title: translate('Dagen'),
            dataIndex: 'n_days',
            key: 'n_days',
            width: 80,
        },
        {
            title: translate('Moment'),
            dataIndex: 'event',
            key: 'event',
            render: (event) => getEventLabel(event),
        },
        ...(hasTeam ? [{
            title: translate('Tijd (min)'),
            dataIndex: 'time',
            key: 'time',
            width: 100,
            render: (time) => time ? time.toFixed(2) : '-',
        }] : []),
        {
            title: translate('Beschrijving'),
            dataIndex: 'description',
            key: 'description',
            ellipsis: true,
        },
        {
            title: '',
            key: 'actions',
            width: 60,
            render: (_, record) => (
                <Popconfirm
                    title={translate('Weet je zeker dat je deze taak wilt verwijderen?')}
                    onConfirm={() => handleDeleteTask(record.id)}
                    okText={translate('Ja')}
                    cancelText={translate('Nee')}
                >
                    <Button type="text" danger icon={<DeleteOutlined />} />
                </Popconfirm>
            ),
        },
    ];

    if (!cropId) {
        return (
            <div style={{ padding: '20px 0', textAlign: 'center', color: '#999' }}>
                {translate('Sla eerst het gewas op om taken toe te voegen')}
            </div>
        );
    }

    return (
        <div>
            {hasTeam && (
                <>
                    <h4 style={{ marginBottom: '16px' }}>{translate('Tijden')}</h4>
                    <Tooltip title={translate("Deze tijden zijn per plant, dus als je er 10 minuten over doet om 100 stuks te zaaien, vul dan 0.1 in.")}>
                        <p style={{ marginBottom: '16px', color: '#666' }}>
                            {translate("Configureer de tijd per plant voor verschillende taken.")} <span style={{ textDecoration: 'underline', cursor: 'help' }}>{translate("Meer info")}</span>
                        </p>
                    </Tooltip>
                    <Row gutter={12}>
                        {!isSeedling && (
                            <Col span={8}>
                                <Form.Item name="time_seeding" label={translate("Tijd zaaien (min)")}>
                                    <InputNumber
                                        style={{ width: '100%' }}
                                        placeholder={translate("Bijv. 0.5")}
                                        min={0}
                                        step={0.01}
                                        precision={2}
                                    />
                                </Form.Item>
                            </Col>
                        )}
                        {!isDirectSown && (
                            <Col span={8}>
                                <Form.Item name="time_planting" label={translate("Tijd planten (min)")}>
                                    <InputNumber
                                        style={{ width: '100%' }}
                                        placeholder={translate("Bijv. 1.0")}
                                        min={0}
                                        step={0.01}
                                        precision={2}
                                    />
                                </Form.Item>
                            </Col>
                        )}
                        <Col span={8}>
                            <Form.Item name="time_harvesting" label={translate("Tijd oogsten (min)")}>
                                <InputNumber
                                    style={{ width: '100%' }}
                                    placeholder={translate("Bijv. 0.25")}
                                    min={0}
                                    step={0.01}
                                    precision={2}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                </>
            )}

            <h4 style={{ marginTop: hasTeam ? '24px' : '0', marginBottom: '16px' }}>{translate('Automatische taken')}</h4>
            <p style={{ marginBottom: '16px', color: '#666' }}>
                {translate('Configureer automatische taken voor dit gewas. Deze taken worden aangemaakt voor elke planting van dit gewas.')}
            </p>

            <Table
                dataSource={tasks}
                columns={columns}
                rowKey="id"
                loading={loading}
                pagination={false}
                size="small"
                locale={{
                    emptyText: translate('Nog geen taken geconfigureerd')
                }}
            />

            <h4 style={{ marginTop: '24px', marginBottom: '16px' }}>{translate('Taak toevoegen')}</h4>
            <Form
                form={form}
                layout="vertical"
                onFinish={handleAddTask}
                onSubmitCapture={handleFormSubmit}
            >
                <Form.Item
                    name="title"
                    label={translate('Titel')}
                    rules={[{ required: true, message: translate('Vul een titel in') }]}
                >
                    <Input placeholder={translate('Bijv. Onkruid wieden')} />
                </Form.Item>

                <Row gutter={16}>
                    <Col span={hasTeam ? 6 : 8}>
                        <Form.Item
                            name="n_days"
                            label={translate('Aantal dagen')}
                            rules={[{ required: true, message: translate('Vul het aantal dagen in') }]}
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                                placeholder={translate('Bijv. 2')}
                                min={0}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={hasTeam ? 12 : 16}>
                        <Form.Item
                            name="event"
                            label={translate('Moment')}
                            rules={[{ required: true, message: translate('Selecteer een moment') }]}
                        >
                            <Select
                                placeholder={translate('Selecteer moment')}
                                options={availableEvents}
                            />
                        </Form.Item>
                    </Col>
                    {hasTeam && (
                        <Col span={6}>
                            <Form.Item
                                name="time"
                                label={translate('Tijd (min)')}
                                tooltip={translate("Deze tijd is per plant, dus als je er 10 minuten over doet om 100 stuks te doen, vul dan 0.1 in.")}
                            >
                                <InputNumber
                                    style={{ width: '100%' }}
                                    placeholder={translate('Bijv. 0.5')}
                                    min={0}
                                    step={0.01}
                                    precision={2}
                                />
                            </Form.Item>
                        </Col>
                    )}
                </Row>

                <Form.Item
                    name="description"
                    label={translate('Beschrijving (optioneel)')}
                >
                    <TextArea
                        rows={2}
                        placeholder={translate('Optionele beschrijving van de taak')}
                    />
                </Form.Item>

                <Form.Item>
                    <Button type="primary" htmlType="submit" icon={<PlusOutlined />}>
                        {translate('Taak toevoegen')}
                    </Button>
                </Form.Item>
            </Form>
        </div>
    );
}
